import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// How often to poll for a new deployment while the tab is open.
const POLL_INTERVAL_MS = 30 * 1000; // 30 seconds

/**
 * Detects when a newer version of the app has been deployed while the current
 * tab is still open.
 *
 * The build stamps its id into `__BUILD_ID__` (see vite.config.ts) and emits a
 * `version.json` alongside the bundle. We fetch that file (never from cache),
 * compare its `buildId` to the one baked into the running code, and flag when
 * they differ so the UI can prompt the user to refresh.
 *
 * Returns `true` once a newer build is detected; otherwise `false`.
 */
export default function useVersionCheck(): boolean {
  const location = useLocation();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Skip in dev / test where there is no deployed version.json and the build
    // id is not meaningful.
    if (import.meta.env.DEV || import.meta.env.MODE === 'test') {
      return;
    }

    let cancelled = false;

    const checkVersion = async () => {
      if (document.visibilityState === 'hidden') return;
      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: 'no-store'
        });
        if (!response.ok) return;
        const data: { buildId?: string } = await response.json();
        if (cancelled || !data?.buildId) return;
        if (data.buildId !== __BUILD_ID__) {
          setUpdateAvailable(true);
        }
      } catch {
        // Network hiccups are non-fatal; we simply try again next cycle.
      }
    };

    const handleFocus = () => {
      checkVersion();
    };

    checkVersion();
    const intervalId = window.setInterval(checkVersion, POLL_INTERVAL_MS);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pointerdown', handleFocus, { capture: true });
    window.addEventListener('keydown', handleFocus, { capture: true });
    document.addEventListener('visibilitychange', handleFocus);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pointerdown', handleFocus, { capture: true });
      window.removeEventListener('keydown', handleFocus, { capture: true });
      document.removeEventListener('visibilitychange', handleFocus);
    };
  }, [location.pathname, location.search]);

  return updateAvailable;
}
